// Scrolls an onboarding step card to the top of the viewport and keeps it there
// while the page finishes re-flowing.
//
// Opening a step re-flows the accordion for up to a second afterwards: the step
// that was open collapses over 0.35s, the new step's lazily-imported chunk
// resolves and swaps a spinner for full-height content, and async data can add
// fields below it. A single scrollIntoView() call locks onto a pixel offset
// measured before any of that lands, so the card it aimed at drifts out of the
// viewport by the time the animation ends - and because the collapse removes
// height *above* the card, it drifts upward, under the fixed header.
//
// So instead of scrolling to a fixed offset, each frame eases toward wherever
// the card is *now*, and a settle window afterwards corrects a late reflow. The
// gap left for the fixed header is the card's own `scroll-margin-top`, so CSS
// stays the single source of truth for it, exactly as with scrollIntoView.

const EASE_DURATION_MS = 350;
const SETTLE_DURATION_MS = 900;
// Sub-pixel scroll positions are never worth a correction, and re-issuing
// scrollTo for one would keep the loop busy for the whole settle window.
const ALIGNMENT_TOLERANCE_PX = 1;

const easeOutCubic = (progress) => 1 - (1 - progress) ** 3;

const resolveScrollMarginTop = (node) => {
  const margin = parseFloat(window.getComputedStyle(node).scrollMarginTop);
  return Number.isFinite(margin) ? margin : 0;
};

const resolveDestination = (node) => {
  const { top } = node.getBoundingClientRect();
  return Math.max(0, top + window.scrollY - resolveScrollMarginTop(node));
};

/**
 * Brings `node` to the top of the viewport, re-measuring it every frame so the
 * scroll survives the layout shifting underneath it.
 *
 * @returns {() => void} cancels the scroll; safe to call more than once.
 */
export const scrollStepIntoView = (node, options = {}) => {
  const noop = () => {};
  if (!node?.getBoundingClientRect || typeof window === "undefined")
    return noop;

  const {
    easeDurationMs = EASE_DURATION_MS,
    settleDurationMs = SETTLE_DURATION_MS
  } = options;

  // jsdom and very old browsers have no rAF; land on the target in one jump
  // rather than not scrolling at all.
  if (typeof window.requestAnimationFrame !== "function") {
    window.scrollTo(0, resolveDestination(node));
    return noop;
  }

  const origin = window.scrollY;
  let frame = null;
  let startedAt = null;
  let cancelled = false;

  const cancel = () => {
    if (cancelled) return;
    cancelled = true;
    if (frame !== null) window.cancelAnimationFrame(frame);
    // These listeners are the only reason a cancelled scroll would keep the
    // node alive, so drop them on every exit path, not just the user's.
    window.removeEventListener("wheel", cancel);
    window.removeEventListener("touchmove", cancel);
    window.removeEventListener("keydown", cancel);
  };

  // A user who reaches for the wheel, a swipe or the arrow keys mid-scroll has
  // decided where they want to be; never fight them for the scrollbar. Listening
  // for "scroll" itself would be useless here - our own scrollTo raises it.
  window.addEventListener("wheel", cancel, { passive: true });
  window.addEventListener("touchmove", cancel, { passive: true });
  window.addEventListener("keydown", cancel);

  const step = (timestamp) => {
    if (cancelled) return;
    if (startedAt === null) startedAt = timestamp;

    const elapsed = timestamp - startedAt;
    const destination = resolveDestination(node);

    if (elapsed < easeDurationMs) {
      const progress = easeOutCubic(elapsed / easeDurationMs);
      window.scrollTo(0, origin + (destination - origin) * progress);
      frame = window.requestAnimationFrame(step);
      return;
    }

    if (Math.abs(window.scrollY - destination) > ALIGNMENT_TOLERANCE_PX) {
      window.scrollTo(0, destination);
    }

    if (elapsed < easeDurationMs + settleDurationMs) {
      frame = window.requestAnimationFrame(step);
      return;
    }

    cancel();
  };

  frame = window.requestAnimationFrame(step);
  return cancel;
};

export default scrollStepIntoView;
